/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { notificationServices } from '@/Services';
import aiPackaging from '@/Services/aiPackaging.services';
import cogsAnalyticsServices from '@/Services/Analytics Services/cogsAnalytics.services';
import { formatDate, handleResponseStreamDownload, serializeVisibilityForAmountCaption } from '@/Helpers';
import { analyticsAction } from '@/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { dummyData, dummyDispatchData } from '../DummyData/TableData';

export const useAiFmsData = (props) => {
    const { parentTab, formStore, toPodFilter } = props;
    const [cacheTime, setCacheTime] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [note, setNote] = useState(null);
    const lastFetchParams = useRef({});
    const [statsSummary, setStatsSummary] = useState([]);
    const [isGridLoading, setIsGridLoading] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(false);
    const [statsCacheTime, setStatsCacheTime] = useState(null);
    const dispatch = useDispatch();
    const shiftList = useSelector((state) => state?.analytics?.SHIFT_LIST);
    const showInternalData = useSelector((state) => state.navigation.showInternalData);

    useEffect(() => {
        if (shiftList?.length === 0) {
            dispatch(analyticsAction?.getShifts());
        }
    }, []);

    const checkNull = useCallback((value) => {
        if ([-999, '-999', '999', 999, undefined, null, ''].includes(value)) {
            return null;
        }
        if (Number(value) === 0) {
            return null;
        }
        return Number(value);
    }, []);

    const fetchData = useCallback(async (filter, tab = parentTab, refresh = false) => {
        if (!refresh) {
            lastFetchParams.current.fetchData = { filter, tab };
        }

        // Every figure here is read per kitchen, so a call without one is not
        // worth making. (The old condition `!filter?.fromPodFilter === ''`
        // compared a boolean to a string and so never fired.)
        if (!filter?.fromPodFilter?.id) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Kitchen is Mandatory',
            });
            return;
        }

        const currentTabValue = filter?.parentTab?.value ?? tab?.value;
        // const shouldUseDummyData = filter?.isPerUnit || currentTabValue === 'SECONDARY_PACKAGING' || currentTabValue.toLowerCase() === 'dispatched';


        setIsGridLoading(true);
        const dateRange = filter?.dateRange;
        const req = {
            data: {
                dateRangeFilter: {
                    dateType: filter?.timeType?.value,
                    from: moment(dateRange?.from).format('YYYY-MM-DD'),
                    to: moment(dateRange?.to).format('YYYY-MM-DD'),
                },
                selectedTab: filter?.parentTab?.value?.toLowerCase() ?? tab?.value?.toLowerCase(),
                selectedSubTab: filter?.tableTab?.toLowerCase(),
                selectedDataFormat: filter?.selectedTab,
                perUnit: filter?.isPerUnit,
                fromStore: filter?.fromPodFilter?.id
                    ? checkNull(filter?.fromPodFilter?.id)
                    : checkNull(formStore),
                storeId: filter?.toPodFilter?.id
                    ? checkNull(filter?.toPodFilter?.id)
                    : checkNull(toPodFilter),
                shiftId: filter?.shiftId
                    ? checkNull(filter?.shiftId)
                    : null,
                processIds: filter?.allProcessTypeMulti?.id,
                producedVariantIds: [...new Set(filter?.productWithVariant?.productData?.flatMap(item => item?.variantIds))],
                batchIds: filter?.batchIds,
                selectedBatches: (filter?.batchIds || filter?.selectedBatches) ? (filter?.selectedBatches || []) : [],
                isBatchWiseMode: filter?.isBatchWiseMode,
                adjustLeftOver: filter?.adjustLeftOver !== undefined ? filter.adjustLeftOver : true,
                mainTab: filter?.operationalDataView,
                // Planned / Adhoc order split. Sent only when the user has actually picked
                // one — 'combined' is the backend's own default, and omitting it keeps the
                // request identical to before this filter existed (same analytics cache row).
                ...(filter?.orderSource && filter.orderSource !== 'combined'
                    ? { orderSource: filter.orderSource }
                    : {}),
                ...(refresh ? { NO_CACHE: true } : {}),
                ...(showInternalData ? { showInternalData: true } : {}),
            },
        };
        // console.log("my req --> ",req,filter,tab);

        try {
            const response = await aiPackaging?.fetchFmsAnalyticsGrid(req);
            if (response?.statusCode === 200) {
                const { data, note } = response;
                setNote(note || null);
                if (response?.cacheTime) {
                    setCacheTime(response?.cacheTime);
                } else {
                    setCacheTime(null);
                }

                // In Process Operational Data mode we never fall back to dummy
                // data — empty means the user should see the "Request Data" CTA
                // in the table area instead of fake numbers.
                const isProcessMode = filter?.operationalDataView === 'processAnalysis';

                // Check if data is empty or null
                if (!data || (Array.isArray(data?.data) && data?.data.length === 0) ||
                    (Array.isArray(data) && data.length === 0)) {
                    if (isProcessMode) {
                        // Honest empty state — let the page show the CTA.
                        setRawData([]);
                    } else {
                        console.log(currentTabValue, "Response data is empty, using dummy data");
                        // Use appropriate dummy data based on the tab
                        if (currentTabValue.toLowerCase() === 'dispatched') {
                            setRawData(dummyDispatchData?.data);
                        } else if (filter?.isPerUnit) {
                            setRawData(dummyData?.data);
                        }
                    }
                } else {
                    if (refresh) {
                        setCacheTime('Just now');
                    }
                    setRawData(data);
                }
            } else {
                notificationServices?.generateNotification({
                    type: 'error',
                    message: response?.message,
                });
                const isProcessMode = filter?.operationalDataView === 'processAnalysis';
                if (isProcessMode) {
                    setRawData([]);
                } else {
                    // Use dummy data on API error as fallback (inventory mode only)
                    console.log("API error, using dummy data as fallback");
                    if (currentTabValue.toLowerCase() === 'dispatched') {
                        setRawData(dummyDispatchData?.data);
                    } else if (filter?.isPerUnit) {
                        setRawData(dummyData?.data);
                    }
                }
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: error.message || 'Failed to fetch data',
            });
            const isProcessMode = filter?.operationalDataView === 'process';
            if (isProcessMode) {
                setRawData([]);
            } else {
                // Use dummy data on network error as fallback (inventory mode only)
                console.log("Network error, using dummy data as fallback");
                if (currentTabValue.toLowerCase() === 'dispatched') {
                    setRawData(dummyDispatchData?.data);
                } else if (filter?.isPerUnit) {
                    setRawData(dummyData?.data);
                }
            }
        } finally {
            setIsGridLoading(false);
        }
    }, [parentTab, checkNull, formStore, toPodFilter, showInternalData]);

    const fetchCogsAnalysisGraph = useCallback(async (dateRange, shiftId, selectedTab, summaryType = '', refresh = false, extraFilters = {}, isBatchWiseMode = false, dateType = {}) => {
        if (!refresh) {
            lastFetchParams.current.fetchCogsAnalysisGraph = { dateRange, shiftId, selectedTab, summaryType, extraFilters, isBatchWiseMode, dateType };
        }
        setIsStatsLoading(true);
        try {
            const apiCalls = [];
            // Kitchen filter travels as `fromPodFilter` (same shape the grid call
            // uses); the backend reads it as `fromStore`. Pulled out of the raw
            // spread below so the payload carries only the backend's key.
            const { fromPodFilter, ...restExtraFilters } = extraFilters || {};
            const basePayload = {
                avoidGraph: true,
                grouping: {
                    active: true,
                    type: 'geographyWiseNew',
                    currentSelection: 'brand',
                    selection: {},
                },
                shiftId: checkNull(shiftId),
                analysisType: 'BOM',
                isBatchWiseMode,
                batchIds: extraFilters?.batchIds,
                selectedBatches: (extraFilters?.batchIds || extraFilters?.selectedBatches) ? (extraFilters?.selectedBatches || []) : [],
                // Operational data view selector — 'inventory' (default) or
                // 'process'. Sent on every stats fetch so backend can branch.
                mainTab: extraFilters?.operationalDataView ?? 'inventory',
                fromStore: fromPodFilter?.id
                    ? checkNull(fromPodFilter?.id)
                    : checkNull(formStore),
                ...(refresh ? { NO_CACHE: true } : {}),
                ...restExtraFilters,
                ...(showInternalData ? { showInternalData: true } : {}),
            };

            const today = moment().startOf('day');
            apiCalls.push(
                cogsAnalyticsServices?.fetchCogsAnalysisGraph({
                    req: {
                        data: {
                            ...basePayload,
                            summaryType: summaryType,
                            yAxis: selectedTab,
                            dateRangeFilter: {
                                dateType: dateType?.value,
                                from: moment(dateRange?.from).format('YYYY-MM-DD'),
                                to: moment(dateRange?.to).format('YYYY-MM-DD'),
                            },
                        },
                    },
                })
            );

            // if (
            //     today.isBetween(
            //         moment(dateRange?.from).startOf('day'),
            //         moment(dateRange?.to).startOf('day'),
            //         null,
            //         '[]'
            //     )
            // ) {
            //     apiCalls.push(
            //         cogsAnalyticsServices?.fetchCogsAnalysisGraphToday({
            //             req: {
            //                 data: {
            //                     ...basePayload,
            //                     yAxis: selectedTab ?? 'TotalWeight',
            //                     dateRangeFilter: {
            //                         from: today.format('YYYY-MM-DD'),
            //                         to: today.format('YYYY-MM-DD'),
            //                     },
            //                 },
            //             },
            //         })
            //     );
            // }

            const [response, responseToday] = await Promise.all(apiCalls);

            if (response?.statusCode === 200) {
                const mergedStatsData = response?.data?.statsData;
                const processedStats = processStatsData(mergedStatsData);
                const statsSummary = Object?.keys(processedStats)?.map((ele) => {
                    return serializeVisibilityForAmountCaption(processedStats, ele);
                });
                setStatsSummary(statsSummary);
                if (refresh) {
                    setStatsCacheTime('Just now');
                } else {
                    setStatsCacheTime(response?.cacheTime || null);
                }
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Failed to fetch analytics data',
            });
        } finally {
            setIsStatsLoading(false);
        }
    }, [showInternalData, checkNull, formStore]);

    const mergeStatsData = useCallback((historicalData, todayData) => {
        if (!historicalData) return {};
        if (!todayData) return historicalData;

        const mergedData = {
            ...historicalData,
        };

        if (Array.isArray(todayData)) {
            todayData.forEach((todayItem) => {
                const historicalKey = Object.keys(historicalData).find(
                    (key) => historicalData[key].label === todayItem.label
                );

                if (
                    historicalKey &&
                    typeof historicalData[historicalKey].value === 'number' &&
                    typeof todayItem.value === 'number'
                ) {
                    mergedData[historicalKey] = {
                        ...historicalData[historicalKey],
                        value: historicalData[historicalKey].value + todayItem.value,
                    };
                }
            });
        } else {
            Object.keys(historicalData).forEach((key) => {
                if (
                    todayData[key] &&
                    typeof historicalData[key].value === 'number' &&
                    typeof todayData[key].value === 'number'
                ) {
                    mergedData[key] = {
                        ...historicalData[key],
                        value: historicalData[key].value + todayData[key].value,
                    };
                }
            });
        }

        return mergedData;
    }, []);

    const processStatsData = useCallback((statsData) => {
        // if (!statsData) return statsData;

        // const processedData = {
        //     ...statsData,
        // };
        // const idealCostKey = 3;

        // if (!idealCostKey || !processedData[idealCostKey]?.value) {
        //     return processedData;
        // }

        // const idealCost = processedData[idealCostKey].value;

        // Object.keys(processedData).forEach((key) => {
        //     if (key !== idealCostKey && processedData[key].value && idealCost > 0) {
        //         const percentage = Math.round(
        //             (processedData[key].value / idealCost) * 100
        //         );
        //         processedData[key] = {
        //             ...processedData[key],
        //             percentageValue: percentage,
        //         };
        //     }

        // });

        return statsData;
    }, []);

    const handleExport = async (filters, tab) => {
        const fromDate = filters?.dateRange?.from;
        const toDate = filters?.dateRange?.to;
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        if (formattedFromDate === '' || formattedToDate === '' || formattedFromDate === null || formattedToDate === null) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Date is Mandatory',
            });
            return;
        }
        const req = {
            data: {
                filters: {
                    dateRangeFilter: {
                        from: formattedFromDate,
                        to: formattedToDate,
                    },
                    fromStore: filters?.fromPodFilter?.id
                        ? checkNull(filters?.fromPodFilter?.id)
                        : checkNull(formStore),
                    storeId: filters?.toPodFilter?.id
                        ? checkNull(filters?.toPodFilter?.id)
                        : checkNull(toPodFilter),
                    analysisType: tab?.value,

                },
            },
        };
        try {
            const response = await aiPackaging?.aiPacakgingExport(req);
            handleResponseStreamDownload(response, `${tab?.value} ${formattedFromDate} to ${formattedToDate}.csv`);
        } catch (error) {
            notificationServices.generateNotification({
                type: 'error',
                message: error,
            });
        }
    };

    const refresh = useCallback(async () => {
        if (lastFetchParams.current.fetchData) {
            const { filter, tab } = lastFetchParams.current.fetchData;
            fetchData(filter, tab, true);
        }
        if (lastFetchParams.current.fetchCogsAnalysisGraph) {
            const { dateRange, shiftId, selectedTab, summaryType, extraFilters, isBatchWiseMode, dateType } = lastFetchParams.current.fetchCogsAnalysisGraph;
            fetchCogsAnalysisGraph(dateRange, shiftId, selectedTab, summaryType, true, extraFilters, isBatchWiseMode, dateType);
        }
    }, [fetchData, fetchCogsAnalysisGraph, showInternalData]);

    const refreshStats = useCallback(async () => {
        if (lastFetchParams.current.fetchCogsAnalysisGraph) {
            const { dateRange, shiftId, selectedTab, summaryType, extraFilters, isBatchWiseMode, dateType } = lastFetchParams.current.fetchCogsAnalysisGraph;
            fetchCogsAnalysisGraph(dateRange, shiftId, selectedTab, summaryType, true, extraFilters, isBatchWiseMode, dateType);
        }
    }, [fetchCogsAnalysisGraph]);

    return {
        rawData,
        statsSummary,
        isGridLoading,
        isStatsLoading,
        fetchData,
        fetchCogsAnalysisGraph,
        handleExport,
        cacheTime,
        statsCacheTime,
        refresh,
        refreshStats,
        note,
    };
};
